/**
 * TableInfoView for Poker Reborn
 *
 * Displays brand name, divider, and game info on the table felt.
 * Subtle ambient text — always visible but not distracting.
 *
 * Example:
 *        Poker Reborn
 *        ─────────────
 *   Texas Hold'Em No Limit. Blinds: $5/$10
 *
 * @see docs/screens/room/TABLE_INFO.md - Visual specs, styling, data source
 * @see docs/screens/room/TABLE.md - Table layout, seat positions
 */

import { Container, Graphics, Text } from 'pixi.js';
import { getScaledValue } from '@/utils/responsive';
import type { Stakes } from '@/types/game';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Text color */
  fill: 0xffffff,

  /** Font stack for info line */
  fontFamily: 'Roboto, Arial, sans-serif',

  /** Brand name font family */
  brandFontFamily: 'Arvo, Roboto, Arial, sans-serif',

  /** Brand alpha */
  brandAlpha: 0.25,

  /** Info line alpha */
  infoAlpha: 0.2,

  /** Divider alpha */
  dividerAlpha: 0.15,

  /** Brand name font size (responsive breakpoints) */
  brandFontSize: {
    1920: 32,
    1440: 26,
    720: 18,
    375: 14,
  } as Record<number, number>,

  /** Info line font size (responsive breakpoints) */
  infoFontSize: {
    1920: 18,
    1440: 15,
    720: 12,
    375: 9,
  } as Record<number, number>,

  /** Divider width (responsive breakpoints) */
  dividerWidth: {
    1920: 160,
    1440: 130,
    720: 90,
    375: 65,
  } as Record<number, number>,

  /** Gap between brand/info and the divider */
  dividerGap: {
    1920: 8,
    1440: 7,
    720: 5,
    375: 4,
  } as Record<number, number>,
};

// =============================================================================
// GAME TYPE DISPLAY NAMES
// =============================================================================

const GAME_TYPE_NAMES: Record<string, string> = {
  holdem: "Texas Hold'Em No Limit",
  omaha: 'Pot Limit Omaha',
  omaha_hilo: 'Omaha Hi/Lo',
};

// =============================================================================
// TABLE INFO VIEW
// =============================================================================

export class TableInfoView extends Container {
  private brandText: Text;
  private divider: Graphics;
  private infoText: Text;

  constructor() {
    super();

    this.brandText = new Text({
      text: 'Poker Reborn',
      style: {
        fill: STYLES.fill,
        fontSize: 24,
        fontFamily: STYLES.brandFontFamily,
        fontWeight: 'bold',
      },
    });
    this.brandText.anchor.set(0.5);
    this.brandText.alpha = STYLES.brandAlpha;
    this.addChild(this.brandText);

    this.divider = new Graphics();
    this.divider.alpha = STYLES.dividerAlpha;
    this.addChild(this.divider);

    this.infoText = new Text({
      text: '',
      style: {
        fill: STYLES.fill,
        fontSize: 14,
        fontFamily: STYLES.fontFamily,
      },
    });
    this.infoText.anchor.set(0.5);
    this.infoText.alpha = STYLES.infoAlpha;
    this.addChild(this.infoText);
  }

  /**
   * Update the displayed game info.
   */
  setGameInfo(gameType: string, stakes: Stakes): void {
    const displayName = GAME_TYPE_NAMES[gameType] ?? gameType;
    const smallBlindLabel = formatChips(stakes.smallBlind);
    const bigBlindLabel = formatChips(stakes.bigBlind);
    this.infoText.text = `${displayName}. Blinds: $${smallBlindLabel}/$${bigBlindLabel}`;
  }

  /**
   * Update font sizes and spacing for responsive layout.
   */
  resize(screenWidth: number): void {
    const brandFontSize = getScaledValue(screenWidth, STYLES.brandFontSize, { discrete: true });
    const infoFontSize = getScaledValue(screenWidth, STYLES.infoFontSize, { discrete: true });
    const dividerWidth = getScaledValue(screenWidth, STYLES.dividerWidth, { discrete: false });
    const dividerGap = getScaledValue(screenWidth, STYLES.dividerGap, { discrete: false });

    this.brandText.style.fontSize = brandFontSize;
    this.infoText.style.fontSize = infoFontSize;

    // Draw divider line
    this.divider.clear();
    this.divider.moveTo(-dividerWidth / 2, 0);
    this.divider.lineTo(dividerWidth / 2, 0);
    this.divider.stroke({ width: 1, color: STYLES.fill });

    // Position: brand → gap → divider → gap → info
    // Divider is exactly centered between brand bottom and info top
    const brandBottom = brandFontSize / 2;
    const dividerY = brandBottom + dividerGap;
    this.brandText.y = 0;
    this.divider.y = dividerY;
    this.infoText.y = dividerY + dividerGap + infoFontSize / 2;
  }
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Format chip amount for display (e.g., 1000 -> "1,000").
 */
function formatChips(amount: number): string {
  return amount.toLocaleString();
}
